import { useState, useCallback, useMemo } from 'react';
import { AmountFor } from '@bts-web/data-layer/server';
import { valueFromAssetToFiat, valueFromFiatToAsset } from '../../../../common';
import { TTransfersNextStepStateWithActions } from '../../../utils/useTransfersNextStepState.client';
import { validateAmountInput } from '../../../../common/utils/amountInputs/validateAmountInput';

interface HookProps {
  selectedAssetData: TTransfersNextStepStateWithActions['selectedAssetData'];
  selectedNetworkData: TTransfersNextStepStateWithActions['selectedNetworkData'];
}

export const useWithdrawalAmountInput = ({
  selectedAssetData,
  selectedNetworkData,
}: HookProps) => {
  const [amountInputValueType, setInputValueType] = useState(
    'FIAT' as AmountFor,
  );

  const [amountInputValue, setAmountInputValue] = useState('');

  const [amountInputErrors, setAmountInputErrors] = useState({
    minWithdrawalAmountNotReached: false,
    portfolioAmountExceeded: false,
  });

  const amountInputValueWithMinFees = useMemo(() => {
    if (amountInputValue === '') return '0';

    return (
      Number(amountInputValue) +
      (amountInputValueType === 'FIAT'
        ? Number(
            valueFromAssetToFiat(
              selectedNetworkData?.minWithdrawalFee?.value as string,
              selectedAssetData?.price,
            )?.value,
          )
        : Number(selectedNetworkData?.minWithdrawalFee?.value as string))
    ).toFixed(selectedAssetData?.price?.precision);
  }, [
    amountInputValue,
    selectedNetworkData,
    selectedAssetData,
    amountInputValueType,
  ]);

  const validateInputAndCheckForThresholdsErrors = (
    newValue: string,
    type: AmountFor,
  ) => {
    const isIntroducedValueAccepted = validateAmountInput(
      newValue,
      amountInputValueType,
      selectedNetworkData?.maximumTransactionPrecision,
    );

    if (!isIntroducedValueAccepted) return;

    setAmountInputValue(newValue);

    const parsedNewValue = newValue.replace(',', '.'); // user can introduce either ',' or '.' for expressing decimals

    const amountOfUnits =
      type === 'FIAT'
        ? valueFromFiatToAsset(
            parsedNewValue,
            selectedAssetData?.price,
            selectedNetworkData?.maximumTransactionPrecision,
            true,
          )?.value
        : parsedNewValue;

    const amountOfUnitsNum = Number(amountOfUnits);

    const thresholdNum = Number(
      selectedNetworkData?.minWithdrawalThreshold?.value,
    );

    const isMinWithdrawalReached = selectedNetworkData?.minWithdrawalThreshold
      ? amountOfUnitsNum >= thresholdNum
      : true;

    const isPortfolioAmountExceeded =
      Number(amountOfUnits) >
      Number(selectedAssetData?.portfolio.assetBalance?.value);

    setAmountInputErrors({
      minWithdrawalAmountNotReached: !isMinWithdrawalReached,
      portfolioAmountExceeded: isPortfolioAmountExceeded,
    });
  };

  const changeAmountValueType = useCallback(
    (newType: AmountFor) => {
      if (amountInputValue.length > 0) {
        let newValue = '';

        newValue =
          (newType === 'FIAT'
            ? valueFromAssetToFiat(amountInputValue, selectedAssetData?.price)
            : valueFromFiatToAsset(
                amountInputValue,
                selectedAssetData?.price,
                selectedNetworkData?.maximumTransactionPrecision,
              )
          )?.value ?? '0';

        setAmountInputValue(newValue ?? '');
      }

      setInputValueType(newType);
    },
    [amountInputValue, selectedAssetData],
  );

  return {
    amountInputValue,
    amountInputValueWithMinFees,
    amountInputValueType,
    amountInputErrors,
    validateInputAndCheckForThresholdsErrors,
    changeAmountValueType,
  };
};
